(() => {
  const HISTORY_KEY = '__jolBagTabOpen';

  const init = () => {
    const button = document.querySelector('[data-jol-floating-bag]');
    if (!button || button.dataset.jolReady === '1') return;
    button.dataset.jolReady = '1';

    let cartOverlay = null;
    let panel = null;
    let overlayObserver = null;
    let bodyObserver = null;
    let wasOpen = false;
    let closingFromHistory = false;

    const hasHistoryMarker = () => Boolean(history.state && history.state[HISTORY_KEY]);
    const markHistoryOpen = () => {
      if (hasHistoryMarker()) return;
      const current = history.state && typeof history.state === 'object' ? history.state : {};
      history.pushState({ ...current, [HISTORY_KEY]: true }, '', location.href);
    };
    const clearStaleHistoryMarker = () => {
      if (!hasHistoryMarker()) return;
      const next = { ...(history.state || {}) };
      delete next[HISTORY_KEY];
      history.replaceState(next, '', location.href);
    };

    const closeExistingBag = () => {
      const overlay = document.querySelector('[data-jol-cart-overlay].is-open');
      if (!overlay) return;
      const close = overlay.querySelector('[data-jol-overlay-close]');
      if (close) close.click();
      else overlay.classList.remove('is-open');
    };

    const syncOpenState = () => {
      if (!cartOverlay) return;
      const isOpen = cartOverlay.classList.contains('is-open');
      button.classList.toggle('is-bag-open', isOpen);
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      if (isOpen && !wasOpen) {
        markHistoryOpen();
      } else if (!isOpen && wasOpen && !closingFromHistory && hasHistoryMarker()) {
        history.back();
      }
      wasOpen = isOpen;
    };

    const clearSwipeState = () => {
      if (!panel) return;
      panel.classList.remove('jol-cart-swipe-active', 'jol-cart-swipe-reset');
      panel.style.removeProperty('--jol-cart-swipe-x');
    };

    const wireSwipe = () => {
      if (!panel || panel.dataset.jolSwipeReady === '1') return;
      panel.dataset.jolSwipeReady = '1';

      let startX = 0;
      let startY = 0;
      let lastX = 0;
      let dragging = false;
      let intentDecided = false;

      panel.addEventListener('touchstart', (event) => {
        if (!cartOverlay?.classList.contains('is-open') || event.touches.length !== 1) return;
        const touch = event.touches[0];
        startX = lastX = touch.clientX;
        startY = touch.clientY;
        dragging = false;
        intentDecided = false;
        clearSwipeState();
      }, { passive: true });

      panel.addEventListener('touchmove', (event) => {
        if (!cartOverlay?.classList.contains('is-open') || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        lastX = touch.clientX;

        if (!intentDecided && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          intentDecided = true;
          dragging = dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.12;
        }
        if (!dragging) return;

        event.preventDefault();
        panel.classList.add('jol-cart-swipe-active');
        panel.style.setProperty('--jol-cart-swipe-x', `${Math.max(0, dx)}px`);
      }, { passive: false });

      panel.addEventListener('touchend', () => {
        if (!dragging) {
          clearSwipeState();
          return;
        }

        const dx = Math.max(0, lastX - startX);
        const width = panel.getBoundingClientRect().width;
        const threshold = Math.max(64, width * 0.18);
        panel.classList.remove('jol-cart-swipe-active');

        if (dx >= threshold) {
          const animation = panel.animate(
            [{ transform: `translateX(${dx}px)` }, { transform: `translateX(${Math.max(width * 1.08, dx)}px)` }],
            { duration: 170, easing: 'cubic-bezier(.3,.75,.2,1)', fill: 'forwards' }
          );
          animation.finished.catch(() => {}).finally(() => {
            closeExistingBag();
            animation.cancel();
            clearSwipeState();
          });
        } else {
          panel.classList.add('jol-cart-swipe-reset');
          panel.style.setProperty('--jol-cart-swipe-x', `${dx}px`);
          const animation = panel.animate(
            [{ transform: `translateX(${dx}px)` }, { transform: 'translateX(0px)' }],
            { duration: 170, easing: 'cubic-bezier(.22,.78,.2,1)' }
          );
          animation.finished.catch(() => {}).finally(() => {
            animation.cancel();
            clearSwipeState();
          });
        }
      }, { passive: true });

      panel.addEventListener('touchcancel', clearSwipeState, { passive: true });
    };

    const wireExistingBag = () => {
      const overlay = document.querySelector('[data-jol-cart-overlay]');
      if (!overlay) return false;
      if (overlay === cartOverlay) {
        syncOpenState();
        return true;
      }

      overlayObserver?.disconnect();
      cartOverlay = overlay;
      panel = overlay.querySelector('.jol-cart-preview');
      wasOpen = overlay.classList.contains('is-open');
      wireSwipe();

      overlayObserver = new MutationObserver(syncOpenState);
      overlayObserver.observe(overlay, { attributes: true, attributeFilter: ['class'] });
      bodyObserver?.disconnect();
      syncOpenState();
      return true;
    };

    bodyObserver = new MutationObserver(() => wireExistingBag());
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    wireExistingBag();

    // This button is only a lower-reach duplicate of the existing Luck Jar bag trigger.
    // It deliberately does not open Shopify's separate cart drawer.
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const trigger = [...document.querySelectorAll('[data-jol-cart-trigger]')]
        .find((el) => el !== button);
      if (!trigger) return;
      trigger.click();
      requestAnimationFrame(() => wireExistingBag());
    });

    // Any existing top bag trigger should get the same Back-button behavior.
    document.addEventListener('click', (event) => {
      if (!event.target.closest?.('[data-jol-cart-trigger], [data-jol-card-add]')) return;
      requestAnimationFrame(() => wireExistingBag());
    }, true);

    window.addEventListener('popstate', (event) => {
      const overlay = document.querySelector('[data-jol-cart-overlay].is-open');
      if (!overlay) return;
      if (event.state && event.state[HISTORY_KEY]) return;
      closingFromHistory = true;
      closeExistingBag();
      window.setTimeout(() => { closingFromHistory = false; }, 90);
    });

    // If the browser restored a stale marker after navigating away from an open bag,
    // clear it so the next Back action still dismisses the bag before leaving the page.
    requestAnimationFrame(() => {
      if (!document.querySelector('[data-jol-cart-overlay].is-open')) clearStaleHistoryMarker();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
